import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar, Toolbar, IconButton,
  Box, Tooltip, Avatar,
  Popover, Typography, Button,
  TextField, InputAdornment, Select, MenuItem, Menu,
  Slider, Divider, Chip,
  Dialog, DialogContent
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import TvIcon from '@mui/icons-material/Tv';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import GridOnIcon from '@mui/icons-material/GridOn';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DevicesIcon from '@mui/icons-material/Devices';
import MessageIcon from '@mui/icons-material/Message';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import InterpreterModeIcon from '@mui/icons-material/InterpreterMode';
import { QRCodeSVG } from 'qrcode.react';
import {
  Mic, Home, MessageSquare, LogOut, Sparkles, Play, Settings, HelpCircle,
  Info, BookOpen, Tv as TvLucide, Compass, MessageSquareHeart,
  Volume2, VolumeX, Gauge, Music, RotateCcw, ChevronDown, Check, ExternalLink,
  Minimize2, Smartphone, X, FileText
} from 'lucide-react';

import AboutDialog from '../AboutDialog';
import DualScreenTutorialDialog from '../DualScreenTutorialDialog';
import UsageTutorialDialog from '../UsageTutorialDialog';
import ConnectedDevicesModal from '../ConnectedDevicesModal';
import { startInteractiveTour } from '../../utils/interactiveTour';
import LanguageSwitcher from '../common/LanguageSwitcher';
import CopyableUrl from '../common/CopyableUrl';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function HeaderAppBar({
  user,
  openVideoPlayer,
  setCurrentIntroVideo,
  setCurrentTransitionVideo,
  currentIntroVideo,
  currentTransitionVideo,
  showHideModalQR,
  showHideModalMessage,
  videoPlayer,
  userSettings,
  handleGoogleSignOut,
  allowInteractivity,
  setVideoId,
  showModalMessage,
  showModalQR,
  showModalVotesTable,
  showHideModalVotesTable,
  openConfigModal,
  screenSettings = { volume: 100, playbackRate: 1.0, pitch: 0 },
  onUpdateScreenSettings,
  onResetScreenSettings,
  isIntegratedScreen = false,
  onToggleIntegratedScreen,
  isRemoteActive = false,
  onTriggerManualVotation,
  currentPlaylistItem,
  playlist = [],
  videoId,
  hasVotes = false,
  onResetVotes,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [openAboutModal, setOpenAboutModal] = useState(false);
  const [openDualScreenModal, setOpenDualScreenModal] = useState(false);
  const [openUsageModal, setOpenUsageModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const [screenAnchorEl, setScreenAnchorEl] = useState(null);
  const [qrMenuAnchorEl, setQrMenuAnchorEl] = useState(null);
  const [votesMenuAnchorEl, setVotesMenuAnchorEl] = useState(null);
  const [openConnectedDevicesModal, setOpenConnectedDevicesModal] = useState(false);
  const [reExecute, setReExecute] = useState(null);
  const [showingScreen, setShowingScreen] = useState(false);
  const [showRemoteModal, setShowRemoteModal] = useState(false);
  const isConfigPage = location.pathname === '/config';
  const activeLang = (i18n.resolvedLanguage || i18n.language || 'es').toLowerCase();
  const langCode = activeLang.startsWith('en') ? 'en_EN' : 'es_ES';
  const defaultManualTemplate = '/docs/GARAOKE_MANUAL_DE_USUARIO_[{lang}].pdf';
  const rawManualUrl = import.meta.env?.VITE_MANUAL_USUARIO_URL || process.env?.VITE_MANUAL_USUARIO_URL || defaultManualTemplate;
  const manualUsuarioUrl = rawManualUrl.includes('{lang}')
    ? rawManualUrl.replace('{lang}', langCode)
    : rawManualUrl;

  const getRemoteUrl = useCallback(() => {
    const localIp = import.meta.env?.VITE_LOCAL_IP || process.env?.VITE_LOCAL_IP;
    const port = window.location.port ? `:${window.location.port}` : '';
    const host = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') && localIp
      ? `${localIp}${port}`
      : window.location.host;
    return `${window.location.protocol}//${host}/remote`;
  }, []);


  const daemon = useCallback(() => {
    setReExecute(Date());
  }, []);

  useEffect(() => {
    if (isIntegratedScreen) {
      setShowingScreen(true);
    } else if (videoPlayer && videoPlayer.name === 'videoPlayer' && !videoPlayer.closed) {
      setShowingScreen(true);
    } else {
      setShowingScreen(false);
      setVideoId(null);
    }
  }, [videoPlayer, reExecute, setVideoId, isIntegratedScreen]);

  useEffect(() => {
    const inter = setInterval(daemon, 1500);
    return () => clearInterval(inter);
  }, [daemon]);

  const handleCloseAboutModal = () => {
    setOpenAboutModal(false);
  };

  const handleAboutClick = () => {
    setOpenAboutModal(true);
  };

  const userMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const userMenuClose = () => {
    setAnchorEl(null);
  };

  const helpMenuOpen = (event) => {
    setHelpAnchorEl(event.currentTarget);
  };

  const helpMenuClose = () => {
    setHelpAnchorEl(null);
  };

  const screenMenuOpen = (event) => {
    setScreenAnchorEl(event.currentTarget);
  };

  const screenMenuClose = () => {
    setScreenAnchorEl(null);
  };

  const isScreenMenuOpen = Boolean(screenAnchorEl);
  const isHelpMenuOpen = Boolean(helpAnchorEl);
  const open = Boolean(anchorEl);

  const setAndShowHideModalMessage = () => {
    const input = document.getElementById('modalMessage');
    const message = input ? input.value : '';
    showHideModalMessage(message);
  }

  const handleChangeIntro = (e) => {
    if (e.target.value === '__add_intro__') {
      if (openConfigModal) openConfigModal(2);
      return;
    }
    setCurrentIntroVideo(e.target.value);
  }

  const handleChangeTransition = (e) => {
    if (e.target.value === '__add_transition__') {
      if (openConfigModal) openConfigModal(3);
      return;
    }
    setCurrentTransitionVideo(e.target.value);
  }

  const defaultIntro = process.env.VITE_DEFAULT_INTRO_VIDEO_CODE || process.env.REACT_APP_DEFAULT_INTRO_VIDEO_CODE || 'H_bB0sAqLNg';
  const defaultTransition = process.env.VITE_DEFAULT_TRANSITION_VIDEO_CODE || process.env.REACT_APP_DEFAULT_TRANSITION_VIDEO_CODE || 'wpXZZ4f_RKY';

  const truncate50 = (str) => {
    if (!str) return '';
    return str.length > 50 ? str.substring(0, 50) : str;
  };

  const getIntros = () => {
    if (!userSettings?.configs?.intros) return [];
    return userSettings.configs.intros
      .map((intro, index) => {
        if (!intro) return null;
        const introId = typeof intro === 'object' ? intro.id : intro;
        const introName = (typeof intro === 'object' && intro.name) ? intro.name : `Mi Intro ${index + 1}`;
        if (!introId) return null;
        const truncatedName = truncate50(introName);
        return (
          <MenuItem key={`intro_${index}_${introId}`} value={introId} title={truncatedName}>
            {truncatedName}
          </MenuItem>
        );
      })
      .filter(Boolean);
  };

  const getTransitions = () => {
    if (!userSettings?.configs?.transitions) return [];
    return userSettings.configs.transitions
      .map((transition, index) => {
        if (!transition) return null;
        const transId = typeof transition === 'object' ? transition.id : transition;
        const transName = (typeof transition === 'object' && transition.name) ? transition.name : `Mi Transición ${index + 1}`;
        if (!transId) return null;
        const truncatedName = truncate50(transName);
        return (
          <MenuItem key={`trans_${index}_${transId}`} value={transId} title={truncatedName}>
            {truncatedName}
          </MenuItem>
        );
      })
      .filter(Boolean);
  };

  const getTooltipQRButton = () => {
    let tooltip = showModalQR ? 'Ocultar QR de Apareamiento en pantalla.' : 'Mostrar QR de Apareamiento en pantalla.';
    if (!allowInteractivity) {
      tooltip += ' Para activar esta función es necesario que se encuentre habilitado [Permitir monitor de interactividad] desde la configuración.';
    }
    return tooltip;
  };

  const getTooltipVotesTable = () => {
    let tooltip = showModalVotesTable ? 'Ocultar Tabla de Resultados en pantalla.' : 'Mostrar Tabla de Resultados en pantalla.';
    if (!allowInteractivity) {
      tooltip += ' Para activar esta función es necesario que se encuentre habilitado [Permitir monitor de interactividad] desde la configuración.';
    }
    return tooltip;
  };

  const assignedPerformerName = (() => {
    // 1. Directo desde item o estructura anidada de currentPlaylistItem
    const direct = currentPlaylistItem?.item?.assignedTo || currentPlaylistItem?.assignedTo;
    if (typeof direct === 'string' && direct.trim()) return direct.trim();

    // 2. Desde la playlist activa buscando por videoId
    const activeVid = currentPlaylistItem?.item?.videoId || currentPlaylistItem?.videoId || videoId;
    if (activeVid && Array.isArray(playlist)) {
      const found = playlist.find(it => (it.videoId === activeVid || it.id === activeVid || it.item?.videoId === activeVid));
      const plSinger = found?.assignedTo || found?.item?.assignedTo;
      if (typeof plSinger === 'string' && plSinger.trim()) return plSinger.trim();
    }

    // 3. Fallback a datos persistidos de sesión / Firestore
    try {
      const cvd = userSettings?.currentVideoData;
      if (cvd) {
        const parsed = typeof cvd === 'string' ? JSON.parse(cvd) : cvd;
        if (parsed?.assignedTo && typeof parsed.assignedTo === 'string' && parsed.assignedTo.trim()) {
          return parsed.assignedTo.trim();
        }
      }
      const cps = userSettings?.currentPlaybackState;
      if (cps) {
        const parsed = typeof cps === 'string' ? JSON.parse(cps) : cps;
        if (parsed?.assignedTo && typeof parsed.assignedTo === 'string' && parsed.assignedTo.trim()) {
          return parsed.assignedTo.trim();
        }
      }
    } catch (e) { }

    return '';
  })();

  const hasAssignedPerformer = Boolean(assignedPerformerName && assignedPerformerName.length > 0);

  const isScreenActive = showingScreen || Boolean(videoId) || Boolean(videoPlayer && !videoPlayer.closed);

  const canTriggerManualVotation = Boolean(
    allowInteractivity &&
    hasAssignedPerformer &&
    isScreenActive &&
    typeof onTriggerManualVotation === 'function'
  );

  const getTooltipManualVotation = () => {
    if (!allowInteractivity) return 'Interactividad desactivada en configuración';
    if (!hasAssignedPerformer) return 'El track actual no tiene un cantante asignado para votar';
    if (!isScreenActive) return 'La pantalla principal no está activa';
    return `Lanzar votación en vivo a todos los monitores (${assignedPerformerName})`;
  };

  const getTooltipMessageButton = () => {
    if (!showingScreen) {
      return 'Abrir pantalla de TV en segunda pantalla para enviar mensajes';
    }
    return showModalMessage ? 'Ocultar mensaje en pantalla' : 'Mostrar mensaje en pantalla';
  };

  const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '2.4.0';

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: 'rgba(6, 1, 14, 0.75)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 4px 30px rgba(0, 0, 0, 0.4)',
          zIndex: 10,
        }}
      >
        <Toolbar sx={{ minHeight: '62px !important', px: { xs: 1.5, md: 2.5 }, gap: 1.5 }}>
          {/* Brand Logo idéntico a Landing */}
          <Box
            onClick={handleAboutClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.65rem',
              cursor: 'pointer',
              userSelect: 'none',
              mr: 2,
              flexShrink: 0
            }}
          >
            <div className="landing-navbar__icon-wrap" style={{ width: '2.2rem', height: '2.2rem' }}>
              <Mic className="w-4 h-4 text-white" />
            </div>
            <span className="landing-navbar__title text-gradient" style={{ fontSize: '1.35rem', fontWeight: 800 }}>
              Garaoke
            </span>
            {!isIntegratedScreen && (
              <span className="landing-navbar__version" style={{ fontSize: '0.68rem', padding: '0.1rem 0.45rem' }}>
                v{appVersion}
              </span>
            )}
          </Box>

          {!isConfigPage &&
            <>
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.2, flex: 1, justifyContent: isIntegratedScreen ? 'flex-end' : 'center', pr: isIntegratedScreen ? 1 : 0 }}>

                <Tooltip title={t('header.screenOptions')}>
                  <Box
                    id="tour-projector-btn"
                    onClick={screenMenuOpen}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      color: showingScreen ? '#00ff88' : '#ffffff',
                      background: showingScreen ? 'rgba(0, 255, 136, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid',
                      borderColor: isScreenMenuOpen
                        ? '#00ff88'
                        : showingScreen ? 'rgba(0, 255, 136, 0.45)' : 'rgba(255, 255, 255, 0.12)',
                      boxShadow: showingScreen ? '0 0 10px rgba(0, 255, 136, 0.2)' : 'none',
                      borderRadius: '10px',
                      px: 1,
                      py: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(0, 255, 136, 0.18)',
                        borderColor: '#00ff88',
                        transform: 'scale(1.03)'
                      }
                    }}
                  >
                    <TvIcon fontSize="small" />
                    <ChevronDown size={14} style={{ opacity: 0.7 }} />
                  </Box>
                </Tooltip>

                {!isIntegratedScreen && (
                  <>
                    <Box id="tour-intros-transitions" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Select
                        labelId="select-intro"
                        id="select-intro"
                        value={currentIntroVideo || defaultIntro}
                        onChange={handleChangeIntro}
                        size="small"
                        sx={{
                          width: 175,
                          fontSize: '0.82rem',
                          borderRadius: '10px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover': { background: 'rgba(255, 255, 255, 0.08)' }
                        }}
                      >
                        <MenuItem value={defaultIntro}>{t('header.defaultIntro')}</MenuItem>
                        {getIntros()}
                        <MenuItem
                          value="__add_intro__"
                          sx={{
                            color: '#ff2d9b',
                            fontWeight: 600,
                            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                            fontSize: '0.8rem',
                            '&:hover': { background: 'rgba(255, 45, 155, 0.12)' }
                          }}
                        >
                          {t('header.addIntro')}
                        </MenuItem>
                      </Select>

                      <Tooltip title={t('header.playIntro')}>
                        <IconButton
                          onClick={() => openVideoPlayer(currentIntroVideo || defaultIntro)}
                          sx={{
                            color: '#c7d2fe',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '10px',
                            p: '7px',
                            '&:hover': { color: '#ff2d9b', borderColor: 'rgba(255, 45, 155, 0.4)', background: 'rgba(255, 45, 155, 0.12)' }
                          }}
                        >
                          <Play size={15} fill="currentColor" />
                        </IconButton>
                      </Tooltip>

                      <Select
                        labelId="select-transition"
                        id="select-transition"
                        value={currentTransitionVideo || defaultTransition}
                        onChange={handleChangeTransition}
                        size="small"
                        sx={{
                          width: 175,
                          fontSize: '0.82rem',
                          borderRadius: '10px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                          '&:hover': { background: 'rgba(255, 255, 255, 0.08)' }
                        }}
                      >
                        <MenuItem value={defaultTransition}>{t('header.defaultTransition')}</MenuItem>
                        {getTransitions()}
                        <MenuItem
                          value="__add_transition__"
                          sx={{
                            color: '#00d4ff',
                            fontWeight: 600,
                            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                            fontSize: '0.8rem',
                            '&:hover': { background: 'rgba(0, 212, 255, 0.12)' }
                          }}
                        >
                          {t('header.addTransition')}
                        </MenuItem>
                      </Select>

                      <Tooltip title={t('header.playTransition')}>
                        <IconButton
                          onClick={() => openVideoPlayer(currentTransitionVideo)}
                          sx={{
                            color: '#c7d2fe',
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '10px',
                            p: '7px',
                            '&:hover': { color: '#b44dff', borderColor: 'rgba(180, 77, 255, 0.4)', background: 'rgba(180, 77, 255, 0.12)' }
                          }}
                        >
                          <Play size={15} fill="currentColor" />
                        </IconButton>
                      </Tooltip>
                    </Box>

                    <TextField
                      placeholder="Mensaje en pantalla..."
                      id='modalMessage'
                      variant="outlined"
                      size="small"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (showingScreen) {
                            setAndShowHideModalMessage();
                          }
                        }
                      }}
                      sx={{
                        width: 175,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '10px',
                          fontSize: '0.82rem',
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          color: '#ffffff',
                          '& fieldset': { border: 'none' },
                          '& input::placeholder': {
                            color: 'rgba(255, 255, 255, 0.45)',
                            opacity: 1
                          }
                        }
                      }}
                    />

                    <Tooltip title={getTooltipMessageButton()}>
                      <span>
                        <IconButton
                          onClick={() => setAndShowHideModalMessage()}
                          disabled={!showingScreen}
                          sx={{
                            color: showModalMessage ? '#ff2d9b' : 'rgba(255, 255, 255, 0.65)',
                            background: showModalMessage ? 'rgba(255, 45, 155, 0.22)' : 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid',
                            borderColor: showModalMessage ? '#ff2d9b' : 'rgba(255, 255, 255, 0.1)',
                            boxShadow: showModalMessage ? '0 0 16px rgba(255, 45, 155, 0.65), inset 0 0 8px rgba(255, 45, 155, 0.25)' : 'none',
                            borderRadius: '10px',
                            p: '7px',
                            transform: showModalMessage ? 'scale(1.05)' : 'scale(1)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: '#ff2d9b',
                              borderColor: 'rgba(255, 45, 155, 0.5)',
                              background: 'rgba(255, 45, 155, 0.18)',
                              transform: 'scale(1.08)'
                            },
                            '&.Mui-disabled': { opacity: 0.35 }
                          }}
                        >
                          <MessageSquare size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={t('header.monitorOptions')}>
                      <span>
                        <Box
                          id="tour-monitor-qr-btn"
                          onClick={(e) => {
                            if (showingScreen) setQrMenuAnchorEl(e.currentTarget);
                          }}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.4,
                            color: showModalQR || Boolean(qrMenuAnchorEl) ? '#00d4ff' : 'rgba(255, 255, 255, 0.65)',
                            background: showModalQR || Boolean(qrMenuAnchorEl) ? 'rgba(0, 212, 255, 0.22)' : 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid',
                            borderColor: Boolean(qrMenuAnchorEl) || showModalQR ? '#00d4ff' : 'rgba(255, 255, 255, 0.1)',
                            boxShadow: showModalQR || Boolean(qrMenuAnchorEl) ? '0 0 16px rgba(0, 212, 255, 0.65), inset 0 0 8px rgba(0, 212, 255, 0.25)' : 'none',
                            borderRadius: '10px',
                            px: 1,
                            py: '6px',
                            cursor: showingScreen ? 'pointer' : 'default',
                            opacity: showingScreen ? 1 : 0.35,
                            transform: showModalQR || Boolean(qrMenuAnchorEl) ? 'scale(1.05)' : 'scale(1)',
                            transition: 'all 0.2s ease',
                            '&:hover': showingScreen ? {
                              color: '#00d4ff',
                              borderColor: 'rgba(0, 212, 255, 0.5)',
                              background: 'rgba(0, 212, 255, 0.18)',
                              transform: 'scale(1.05)'
                            } : {}
                          }}
                        >
                          <QrCodeIcon fontSize="small" />
                          <ChevronDown size={14} style={{ opacity: 0.7 }} />
                        </Box>
                      </span>
                    </Tooltip>

                    <Menu
                      anchorEl={qrMenuAnchorEl}
                      open={Boolean(qrMenuAnchorEl)}
                      onClose={() => setQrMenuAnchorEl(null)}
                      PaperProps={{
                        sx: {
                          backgroundColor: '#120b22 !important',
                          background: '#120b22 !important',
                          border: '1px solid rgba(0, 212, 255, 0.25) !important',
                          borderRadius: '14px !important',
                          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 212, 255, 0.12) !important',
                          color: '#ffffff',
                          minWidth: '220px',
                          p: '6px 8px !important',
                          '& .MuiMenuItem-root': {
                            fontSize: '0.8rem !important',
                            fontWeight: '500 !important',
                            borderRadius: '8px',
                            py: '7px !important',
                            px: '10px !important',
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 1.4,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 212, 255, 0.12)',
                              color: '#00d4ff',
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setQrMenuAnchorEl(null);
                          showHideModalQR();
                        }}
                        sx={{
                          fontSize: '0.8rem !important',
                          fontWeight: '500 !important',
                          py: '7px !important',
                          px: '10px !important',
                        }}
                      >
                        <QrCodeIcon sx={{ fontSize: 17, color: '#00d4ff', mr: 1.5, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem' }}>
                          {showModalQR ? t('header.hideQrScreen', 'Ocultar QR en Pantalla') : t('header.showQrScreen', 'Mostrar QR en Pantalla')}
                        </span>
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setQrMenuAnchorEl(null);
                          setOpenConnectedDevicesModal(true);
                        }}
                        sx={{
                          fontSize: '0.8rem !important',
                          fontWeight: '500 !important',
                          py: '7px !important',
                          px: '10px !important',
                        }}
                      >
                        <DevicesIcon sx={{ fontSize: 17, color: '#00d4ff', mr: 1.5, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem' }}>
                          {t('header.viewLinkedMonitors', 'Ver Monitores Vinculados')}
                        </span>
                      </MenuItem>
                    </Menu>

                    {/* Botón y Submenú de Tabla de Votos */}
                    <Tooltip title={t('screenControls.votesTooltip', 'Opciones de tabla de votaciones')}>
                      <span>
                        <IconButton
                          onClick={(e) => setVotesMenuAnchorEl(e.currentTarget)}
                          disabled={!showingScreen}
                          sx={{
                            color: showModalVotesTable ? '#b44dff' : 'rgba(255, 255, 255, 0.65)',
                            background: showModalVotesTable ? 'rgba(180, 77, 255, 0.22)' : 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid',
                            borderColor: showModalVotesTable ? '#b44dff' : 'rgba(255, 255, 255, 0.1)',
                            boxShadow: showModalVotesTable ? '0 0 16px rgba(180, 77, 255, 0.65), inset 0 0 8px rgba(180, 77, 255, 0.25)' : 'none',
                            borderRadius: '10px',
                            p: '7px',
                            transform: showModalVotesTable ? 'scale(1.05)' : 'scale(1)',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: '#b44dff',
                              borderColor: 'rgba(180, 77, 255, 0.5)',
                              background: 'rgba(180, 77, 255, 0.18)',
                              transform: 'scale(1.08)'
                            },
                            '&.Mui-disabled': { opacity: 0.35 }
                          }}
                        >
                          <GridOnIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Menu
                      anchorEl={votesMenuAnchorEl}
                      open={Boolean(votesMenuAnchorEl)}
                      onClose={() => setVotesMenuAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                      PaperProps={{
                        sx: {
                          backgroundColor: '#120b22 !important',
                          background: '#120b22 !important',
                          border: '1px solid rgba(180, 77, 255, 0.3) !important',
                          borderRadius: '14px !important',
                          boxShadow: '0 16px 40px rgba(0, 0, 0, 0.9), 0 0 20px rgba(180, 77, 255, 0.15) !important',
                          color: '#ffffff',
                          minWidth: '235px',
                          p: '6px 8px !important',
                          '& .MuiMenuItem-root': {
                            fontSize: '0.8rem !important',
                            fontWeight: '500 !important',
                            borderRadius: '8px',
                            py: '8px !important',
                            px: '10px !important',
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: 1.4,
                            '&:hover': {
                              backgroundColor: 'rgba(180, 77, 255, 0.15)',
                              color: '#b44dff',
                            },
                            '&.Mui-disabled': {
                              opacity: 0.35,
                            }
                          }
                        }
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          setVotesMenuAnchorEl(null);
                          showHideModalVotesTable();
                        }}
                      >
                        <GridOnIcon sx={{ fontSize: 17, color: '#b44dff', mr: 1.5, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem' }}>
                          {showModalVotesTable ? t('screenControls.hideVotesTable', 'Ocultar Tabla de Resultados') : t('screenControls.showVotesTable', 'Mostrar Tabla de Resultados')}
                        </span>
                      </MenuItem>

                      <MenuItem
                        onClick={() => {
                          setVotesMenuAnchorEl(null);
                          if (onResetVotes) onResetVotes();
                        }}
                        disabled={!hasVotes}
                        sx={{
                          color: hasVotes ? '#ff4d6d !important' : 'rgba(255, 255, 255, 0.35) !important',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 77, 109, 0.15) !important',
                            color: '#ff4d6d !important',
                          }
                        }}
                      >
                        <RestartAltIcon sx={{ fontSize: 18, color: hasVotes ? '#ff4d6d' : 'rgba(255, 255, 255, 0.3)', mr: 1.5, flexShrink: 0 }} />
                        <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                          {t('screenControls.resetVotes', 'Reiniciar Votaciones')}
                        </span>
                      </MenuItem>
                    </Menu>

                    <Tooltip title={getTooltipManualVotation()}>
                      <span>
                        <IconButton
                          onClick={() => onTriggerManualVotation && onTriggerManualVotation()}
                          disabled={!canTriggerManualVotation}
                          sx={{
                            color: canTriggerManualVotation ? '#ff2d9b' : 'rgba(255, 255, 255, 0.4)',
                            background: canTriggerManualVotation ? 'rgba(255, 45, 155, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid',
                            borderColor: canTriggerManualVotation ? 'rgba(255, 45, 155, 0.45)' : 'rgba(255, 255, 255, 0.1)',
                            boxShadow: canTriggerManualVotation ? '0 0 16px rgba(255, 45, 155, 0.5), inset 0 0 8px rgba(255, 45, 155, 0.2)' : 'none',
                            borderRadius: '10px',
                            p: '7px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: '#ffffff',
                              borderColor: '#ff2d9b',
                              background: 'rgba(255, 45, 155, 0.35)',
                              boxShadow: '0 0 20px rgba(255, 45, 155, 0.7)',
                              transform: 'scale(1.08)'
                            },
                            '&.Mui-disabled': { opacity: 0.35 }
                          }}
                        >
                          <HowToVoteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title={isRemoteActive ? t('header.remoteActive', 'Control Remoto Conectado') : t('header.remoteInactive', 'Control Remoto Móvil (Smartphone)')}>
                      <span>
                        <IconButton
                          onClick={() => setShowRemoteModal(true)}
                          sx={{
                            color: isRemoteActive ? '#00ffd5' : 'rgba(255, 255, 255, 0.65)',
                            background: isRemoteActive ? 'rgba(0, 255, 213, 0.16)' : 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid',
                            borderColor: isRemoteActive ? '#00ffd5' : 'rgba(255, 255, 255, 0.1)',
                            boxShadow: isRemoteActive ? '0 0 14px rgba(0, 255, 213, 0.45)' : 'none',
                            borderRadius: '10px',
                            p: '7px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              color: '#00ffd5',
                              borderColor: '#00ffd5',
                              background: 'rgba(0, 255, 213, 0.22)',
                              transform: 'scale(1.08)',
                              boxShadow: '0 0 16px rgba(0, 255, 213, 0.45)',
                            },
                          }}
                        >
                          <Smartphone size={16} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}

              </Box>

              <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1.2, flexShrink: 0 }}>
                {/* Botón de Ayuda (Menos prominente que avatar) */}
                <Tooltip title="Ayuda y Tutoriales">
                  <IconButton
                    id="tour-help-menu"
                    size="small"
                    onClick={helpMenuOpen}
                    sx={{
                      color: isHelpMenuOpen ? '#00f0ff' : 'rgba(255, 255, 255, 0.7)',
                      background: isHelpMenuOpen ? 'rgba(0, 240, 255, 0.15)' : 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid',
                      borderColor: isHelpMenuOpen ? 'rgba(0, 240, 255, 0.4)' : 'rgba(255, 255, 255, 0.08)',
                      borderRadius: '10px',
                      p: '6px',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        color: '#00f0ff',
                        background: 'rgba(0, 240, 255, 0.1)',
                        borderColor: 'rgba(0, 240, 255, 0.3)'
                      }
                    }}
                  >
                    <HelpCircle size={18} />
                  </IconButton>
                </Tooltip>

                {/* Menú desplegable de Ayuda */}
                <Popover
                  id="helpMenu"
                  open={isHelpMenuOpen}
                  anchorEl={helpAnchorEl}
                  onClose={helpMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        backgroundColor: '#120b22 !important',
                        background: '#120b22 !important',
                        border: '1px solid rgba(0, 240, 255, 0.2) !important',
                        borderRadius: '14px !important',
                        boxShadow: '0 16px 40px rgba(0, 0, 0, 0.9), 0 0 20px rgba(0, 240, 255, 0.1) !important',
                        mt: 1.2,
                        minWidth: 260,
                        p: '12px 10px !important'
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ px: 1.5, py: 0.8, mb: 0.5, borderBottom: '1px solid rgba(255, 255, 255, 0.06)' }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'rgba(0, 240, 255, 0.9)' }}>
                        {t('header.helpCenter', 'Centro de Ayuda')}
                      </Typography>
                    </Box>

                    {/* 1. Tutorial de uso y funciones */}
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => {
                        helpMenuClose();
                        setOpenUsageModal(true);
                      }}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        color: '#ffffff',
                        borderRadius: '8px',
                        fontSize: '0.82rem',
                        py: 0.7,
                        px: 1.2,
                        gap: 1.2,
                        '&:hover': { background: 'rgba(168, 85, 247, 0.12)', color: '#c084fc' }
                      }}
                    >
                      <BookOpen size={16} color="#c084fc" />
                      {t('header.usageTutorial', 'Tutorial de uso y funciones')}
                    </Button>

                    {/* 2. Manual de usuario (PDF) */}
                    <Button
                      fullWidth
                      size="small"
                      component="a"
                      href={manualUsuarioUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        helpMenuClose();
                      }}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        color: '#ffffff',
                        borderRadius: '8px',
                        fontSize: '0.82rem',
                        py: 0.7,
                        px: 1.2,
                        gap: 1.2,
                        '&:hover': { background: 'rgba(255, 87, 87, 0.12)', color: '#ff5757' }
                      }}
                    >
                      <FileText size={16} color="#ff5757" />
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <span>{t('header.userManual', 'Manual de usuario (PDF)')}</span>
                        <ExternalLink size={12} style={{ opacity: 0.6 }} />
                      </Box>
                    </Button>

                    {/* 3. Acerca de Garaoke */}
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => {
                        helpMenuClose();
                        setOpenAboutModal(true);
                      }}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        color: '#ffffff',
                        borderRadius: '8px',
                        fontSize: '0.82rem',
                        py: 0.7,
                        px: 1.2,
                        gap: 1.2,
                        '&:hover': { background: 'rgba(0, 240, 255, 0.12)', color: '#00f0ff' }
                      }}
                    >
                      <Info size={16} color="#00f0ff" />
                      {t('header.about', 'Acerca de Garaoke')}
                    </Button>

                    {/* 4. Paseo Interactivo */}
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => {
                        helpMenuClose();
                        setTimeout(() => {
                          startInteractiveTour(true);
                        }, 250);
                      }}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        color: '#ffffff',
                        borderRadius: '8px',
                        fontSize: '0.82rem',
                        py: 0.7,
                        px: 1.2,
                        gap: 1.2,
                        '&:hover': { background: 'rgba(255, 45, 155, 0.12)', color: '#ff2d9b' }
                      }}
                    >
                      <Compass size={16} color="#ff2d9b" />
                      {t('header.interactiveTour', 'Paseo Interactivo')}
                    </Button>

                    {/* 5. Tutorial de conexión a pantalla extendida */}
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => {
                        helpMenuClose();
                        setOpenDualScreenModal(true);
                      }}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        color: '#ffffff',
                        borderRadius: '8px',
                        fontSize: '0.82rem',
                        py: 0.7,
                        px: 1.2,
                        gap: 1.2,
                        '&:hover': { background: 'rgba(0, 240, 120, 0.12)', color: '#00f078' }
                      }}
                    >
                      <TvLucide size={16} color="#00f078" />
                      {t('header.dualScreenTutorial', 'Tutorial de pantalla extendida')}
                    </Button>

                    {/* 6. Soporte y Feedback */}
                    <Button
                      fullWidth
                      size="small"
                      onClick={() => {
                        helpMenuClose();
                        navigate('/support');
                      }}
                      sx={{
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        color: '#ffffff',
                        borderRadius: '8px',
                        fontSize: '0.82rem',
                        py: 0.7,
                        px: 1.2,
                        gap: 1.2,
                        '&:hover': { background: 'rgba(255, 183, 3, 0.12)', color: '#ffb703' }
                      }}
                    >
                      <MessageSquareHeart size={16} color="#ffb703" />
                      {t('header.supportFeedback', 'Soporte y Feedback')}
                    </Button>
                  </Box>
                </Popover>

                {/* Submenú / Popover de Control de Pantalla Secundaria */}
                <Popover
                  open={isScreenMenuOpen}
                  anchorEl={screenAnchorEl}
                  onClose={screenMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        background: 'rgba(15, 8, 29, 0.98)',
                        backdropFilter: 'blur(16px)',
                        border: '1px solid rgba(0, 255, 136, 0.35)',
                        borderRadius: '16px',
                        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.85), 0 0 25px rgba(0, 255, 136, 0.2)',
                        width: 320,
                        p: 2,
                        mt: 1,
                        color: '#ffffff',
                      }
                    }
                  }}
                >
                  {/* Header del Submenú */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.2, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '8px', background: 'rgba(0, 255, 136, 0.15)', border: '1px solid rgba(0, 255, 136, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00ff88' }}>
                        <TvIcon sx={{ fontSize: 17 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#ffffff' }}>
                        {t('header.screenOptions')}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={isIntegratedScreen ? t('common.options') : (showingScreen ? "Live" : t('common.close'))}
                      sx={{
                        height: 20,
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        background: (showingScreen || isIntegratedScreen) ? 'rgba(0, 255, 136, 0.18)' : 'rgba(255, 255, 255, 0.08)',
                        color: (showingScreen || isIntegratedScreen) ? '#00ff88' : 'rgba(255, 255, 255, 0.5)',
                        border: '1px solid',
                        borderColor: (showingScreen || isIntegratedScreen) ? 'rgba(0, 255, 136, 0.4)' : 'transparent'
                      }}
                    />
                  </Box>

                  {/* 1. Acción: Abrir pantalla secundaria en modo flotante (Primero, con degradé magenta/violeta) */}
                  <Box sx={{ pt: 1.5, pb: 0.8 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<ExternalLink size={16} />}
                      onClick={() => {
                        openVideoPlayer(currentTransitionVideo || defaultTransition, true);
                        screenMenuClose();
                      }}
                      sx={{
                        mb: 1.2,
                        textTransform: 'none',
                        borderRadius: '10px',
                        background: (showingScreen && !isIntegratedScreen)
                          ? 'linear-gradient(135deg, rgba(255, 45, 155, 0.3) 0%, rgba(180, 77, 255, 0.3) 100%)'
                          : 'linear-gradient(135deg, #ff2d9b 0%, #b44dff 100%)',
                        color: '#ffffff',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        py: 0.9,
                        border: '1px solid',
                        borderColor: (showingScreen && !isIntegratedScreen) ? 'rgba(255, 45, 155, 0.6)' : 'transparent',
                        boxShadow: (showingScreen && !isIntegratedScreen) ? '0 0 12px rgba(255, 45, 155, 0.3)' : '0 4px 16px rgba(255, 45, 155, 0.4)',
                        '&:hover': {
                          background: (showingScreen && !isIntegratedScreen)
                            ? 'rgba(255, 45, 155, 0.45)'
                            : 'linear-gradient(135deg, #ff47ac 0%, #c466ff 100%)',
                          boxShadow: '0 6px 20px rgba(255, 45, 155, 0.55)',
                        }
                      }}
                    >
                      {t('header.openFloating')}
                    </Button>

                    {/* 2. Acción: Modo Pantalla Integrada (30% / 70%) (Segundo, con degradé verde/cian) */}
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={isIntegratedScreen ? <Minimize2 size={16} /> : <TvLucide size={16} />}
                      onClick={() => {
                        if (onToggleIntegratedScreen) {
                          onToggleIntegratedScreen(!isIntegratedScreen);
                        }
                        screenMenuClose();
                      }}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        background: isIntegratedScreen
                          ? 'linear-gradient(135deg, rgba(0, 255, 136, 0.25) 0%, rgba(0, 212, 255, 0.25) 100%)'
                          : 'linear-gradient(135deg, #00ff88 0%, #00d4ff 100%)',
                        color: isIntegratedScreen ? '#00ff88' : '#0a0a0f',
                        fontWeight: 700,
                        fontSize: '0.82rem',
                        py: 0.9,
                        border: '1px solid',
                        borderColor: isIntegratedScreen ? 'rgba(0, 255, 136, 0.5)' : 'transparent',
                        boxShadow: isIntegratedScreen ? 'none' : '0 4px 16px rgba(0, 255, 136, 0.35)',
                        '&:hover': {
                          background: isIntegratedScreen
                            ? 'rgba(0, 255, 136, 0.35)'
                            : 'linear-gradient(135deg, #1aff96 0%, #1ae0ff 100%)',
                          boxShadow: '0 6px 20px rgba(0, 255, 136, 0.5)',
                        }
                      }}
                    >
                      {isIntegratedScreen ? t('header.detachFloating') : t('header.openIntegrated')}
                    </Button>
                  </Box>

                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', my: 1.2 }} />

                  {/* 2. Control de Volumen del Reproductor (independiente de efectos) */}
                  <Box sx={{ py: 0.8 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        {screenSettings.volume === 0 ? (
                          <VolumeX size={15} color="#ff2d9b" />
                        ) : (
                          <Volume2 size={15} color="#00ff88" />
                        )}
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }}>
                          {t('header.screenVolume')}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#00ff88' }}>
                        {screenSettings.volume}%
                      </Typography>
                    </Box>
                    <Slider
                      size="small"
                      value={screenSettings.volume ?? 100}
                      min={0}
                      max={100}
                      step={1}
                      onChange={(e, val) => {
                        if (onUpdateScreenSettings) {
                          onUpdateScreenSettings({ volume: val }, false);
                        }
                      }}
                      onChangeCommitted={(e, val) => {
                        if (onUpdateScreenSettings) {
                          onUpdateScreenSettings({ volume: val }, true);
                        }
                      }}
                      sx={{
                        color: '#00ff88',
                        height: 5,
                        '& .MuiSlider-thumb': {
                          width: 14,
                          height: 14,
                          boxShadow: '0 0 8px #00ff88',
                          '&:hover, &.Mui-focusVisible': {
                            boxShadow: '0 0 12px #00ff88',
                          },
                        },
                        '& .MuiSlider-track': {
                          background: 'linear-gradient(90deg, #00ff88 0%, #00d4ff 100%)',
                        },
                        '& .MuiSlider-rail': {
                          backgroundColor: 'rgba(255, 255, 255, 0.12)',
                        }
                      }}
                    />
                    <Typography sx={{ fontSize: '0.67rem', color: 'rgba(255, 255, 255, 0.45)', mt: -0.5 }}>
                      {t('header.screenOptions')}
                    </Typography>
                  </Box>

                  {/* 3. Control de Velocidad de Reproducción */}
                  <Box sx={{ py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                        <Gauge size={15} color="#00d4ff" />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }}>
                          {t('header.playbackRate')}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#00d4ff' }}>
                        {screenSettings.playbackRate ?? 1.0}x
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 0.6 }}>
                      {[0.5, 0.75, 1.0, 1.25, 1.5].map(rate => {
                        const isSelected = (screenSettings.playbackRate ?? 1.0) === rate;
                        return (
                          <Button
                            key={rate}
                            size="small"
                            onClick={() => {
                              if (onUpdateScreenSettings) {
                                onUpdateScreenSettings({ playbackRate: rate }, true);
                              }
                            }}
                            sx={{
                              minWidth: 0,
                              py: 0.4,
                              px: 0,
                              fontSize: '0.74rem',
                              fontWeight: isSelected ? 700 : 500,
                              borderRadius: '8px',
                              textTransform: 'none',
                              background: isSelected ? 'rgba(0, 212, 255, 0.2)' : 'rgba(255, 255, 255, 0.04)',
                              color: isSelected ? '#00d4ff' : 'rgba(255, 255, 255, 0.65)',
                              border: '1px solid',
                              borderColor: isSelected ? '#00d4ff' : 'rgba(255, 255, 255, 0.08)',
                              boxShadow: isSelected ? '0 0 10px rgba(0, 212, 255, 0.3)' : 'none',
                              '&:hover': {
                                background: 'rgba(0, 212, 255, 0.25)',
                                borderColor: '#00d4ff',
                                color: '#ffffff'
                              }
                            }}
                          >
                            {rate === 1.0 ? '1x' : `${rate}x`}
                          </Button>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* 4. Control de Tono de Audio (Semitonos / Key Shift) - Feature flag */}
                  {(import.meta.env?.VITE_ENABLE_PITCH_CONTROL === 'true' || process.env?.VITE_ENABLE_PITCH_CONTROL === 'true') && (
                    <Box sx={{ py: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.8 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Music size={15} color="#b44dff" />
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.9)' }}>
                            Tono de audio
                          </Typography>
                          <Tooltip
                            arrow
                            placement="top"
                            title={
                              <Box sx={{ p: 0.5 }}>
                                <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: '#b44dff', mb: 0.3 }}>
                                  Control de tono en YouTube
                                </Typography>
                                <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255, 255, 255, 0.88)', lineHeight: 1.35 }}>
                                  Por políticas de seguridad de YouTube, alterar el audio embebido en tiempo real se realiza mediante extensiones de navegador como Transpose. Garaoke guarda tu tono preferido en base de datos.
                                </Typography>
                              </Box>
                            }
                          >
                            <Box sx={{ display: 'inline-flex', cursor: 'pointer', opacity: 0.65, '&:hover': { opacity: 1, color: '#00d4ff' } }}>
                              <HelpCircle size={13} />
                            </Box>
                          </Tooltip>
                        </Box>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#b44dff' }}>
                          {(screenSettings.pitch ?? 0) === 0 ? 'Original (0)' : `${(screenSettings.pitch > 0 ? '+' : '')}${screenSettings.pitch} semitono${Math.abs(screenSettings.pitch) > 1 ? 's' : ''}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                        {[-3, -2, -1, 0, 1, 2, 3].map(pitchVal => {
                          const isSelected = (screenSettings.pitch ?? 0) === pitchVal;
                          return (
                            <Button
                              key={pitchVal}
                              size="small"
                              onClick={() => {
                                if (onUpdateScreenSettings) {
                                  onUpdateScreenSettings({ pitch: pitchVal }, true);
                                }
                              }}
                              sx={{
                                minWidth: 0,
                                py: 0.4,
                                px: 0,
                                fontSize: '0.72rem',
                                fontWeight: isSelected ? 700 : 500,
                                borderRadius: '8px',
                                textTransform: 'none',
                                background: isSelected ? 'rgba(180, 77, 255, 0.25)' : 'rgba(255, 255, 255, 0.04)',
                                color: isSelected ? '#b44dff' : 'rgba(255, 255, 255, 0.65)',
                                border: '1px solid',
                                borderColor: isSelected ? '#b44dff' : 'rgba(255, 255, 255, 0.08)',
                                boxShadow: isSelected ? '0 0 10px rgba(180, 77, 255, 0.35)' : 'none',
                                '&:hover': {
                                  background: 'rgba(180, 77, 255, 0.3)',
                                  borderColor: '#b44dff',
                                  color: '#ffffff'
                                }
                              }}
                            >
                              {pitchVal === 0 ? '0' : (pitchVal > 0 ? `+${pitchVal}` : pitchVal)}
                            </Button>
                          );
                        })}
                      </Box>

                      {/* Aviso informativo de extensión para cambio de frecuencia en YouTube */}
                      <Box
                        sx={{
                          mt: 1,
                          p: '6px 10px',
                          borderRadius: '8px',
                          background: 'rgba(180, 77, 255, 0.08)',
                          border: '1px dashed rgba(180, 77, 255, 0.28)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1
                        }}
                      >
                        <Typography sx={{ fontSize: '0.67rem', color: 'rgba(255, 255, 255, 0.65)', lineHeight: 1.25 }}>
                          ¿Cantar en otro tono? Usa la extensión <b>Transpose</b> para modificar el audio de YouTube.
                        </Typography>
                        <Button
                          size="small"
                          component="a"
                          href={import.meta.env?.VITE_PITCH_EXTENSION_URL || process.env?.VITE_PITCH_EXTENSION_URL || 'https://chromewebstore.google.com/detail/transpose-%E2%96%B3%E2%96%BD-pitch-%E2%96%B9-spee/ioimlbgefgadofblnajllknopjboejda'}
                          target="_blank"
                          rel="noopener noreferrer"
                          endIcon={<ExternalLink size={11} />}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            color: '#b44dff',
                            p: '2px 6px',
                            minWidth: 0,
                            flexShrink: 0,
                            borderRadius: '6px',
                            background: 'rgba(180, 77, 255, 0.12)',
                            '&:hover': {
                              background: 'rgba(180, 77, 255, 0.25)',
                              color: '#ffffff'
                            }
                          }}
                        >
                          Instalar extensión
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)', my: 1.2 }} />

                  {/* 5. Footer: Restablecer valores por defecto */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: 0.5 }}>
                    <Button
                      size="small"
                      startIcon={<RotateCcw size={14} />}
                      onClick={() => {
                        if (onResetScreenSettings) {
                          onResetScreenSettings();
                        }
                      }}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.74rem',
                        color: 'rgba(255, 255, 255, 0.65)',
                        borderRadius: '8px',
                        '&:hover': {
                          color: '#ff2d9b',
                          background: 'rgba(255, 45, 155, 0.12)'
                        }
                      }}
                    >
                      {t('header.resetScreen')}
                    </Button>

                    <Typography sx={{ fontSize: '0.68rem', color: 'rgba(255, 255, 255, 0.4)' }}>
                      Auto-save
                    </Typography>
                  </Box>
                </Popover>

                {/* Botón de Configuración */}
                <Tooltip title={t('header.settings')}>
                  <IconButton
                    size="small"
                    onClick={() => openConfigModal && openConfigModal(0)}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.7)',
                      background: 'rgba(255, 255, 255, 0.04)',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      borderRadius: '10px',
                      p: '6px',
                      '&:hover': { color: '#ffffff', background: 'rgba(255, 255, 255, 0.08)' }
                    }}
                  >
                    <SettingsIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* Avatar del usuario con halo de brillo sutil interactivo */}
                <Tooltip title="Cuenta y opciones">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      padding: '2px',
                      background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.6) 0%, rgba(180, 77, 255, 0.6) 100%)',
                      boxShadow: '0 0 14px rgba(255, 45, 155, 0.35)',
                      transition: 'all 0.25s ease',
                      '&:hover': {
                        transform: 'scale(1.08)',
                        boxShadow: '0 0 20px rgba(255, 45, 155, 0.65)'
                      }
                    }}
                    onClick={userMenuOpen}
                  >
                    <Avatar
                      alt="Avatar"
                      src={user?.photoURL}
                      sx={{ width: 34, height: 34, border: '2px solid #0d0617' }}
                    />
                  </Box>
                </Tooltip>

                <Popover
                  id='userMenu'
                  open={open}
                  anchorEl={anchorEl}
                  onClose={userMenuClose}
                  className="user-menu-popover"
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  slotProps={{
                    paper: {
                      className: "user-avatar-popover",
                      sx: {
                        backgroundColor: '#130924 !important',
                        background: '#130924 !important',
                        border: '1px solid rgba(255, 255, 255, 0.16) !important',
                        borderRadius: '18px !important',
                        boxShadow: '0 24px 60px rgba(0, 0, 0, 0.98), 0 0 25px rgba(180, 77, 255, 0.15) !important',
                        backdropFilter: 'none !important',
                        WebkitBackdropFilter: 'none !important',
                        overflow: 'hidden',
                        mt: 1.5,
                        minWidth: 290,
                        p: '22px 20px !important'
                      }
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Header de perfil de usuario */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1.8, borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <Avatar
                        alt="Avatar"
                        src={user?.photoURL}
                        sx={{ width: 44, height: 44, border: '2px solid #ff2d9b' }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#ffffff', fontSize: '0.92rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user?.displayName || 'Usuario de Garaoke'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                          {user?.email || 'Sin correo registrado'}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Banner Pro "Mejora a Plan Pro" */}
                    <Link to="/pricing" style={{ textDecoration: 'none' }} onClick={userMenuClose}>
                      <Box sx={{
                        p: '12px 14px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.25) 0%, rgba(180, 77, 255, 0.25) 100%)',
                        border: '1px solid rgba(255, 45, 155, 0.45)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 4px 14px rgba(255, 45, 155, 0.2)',
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          borderColor: '#ff2d9b',
                          boxShadow: '0 6px 20px rgba(255, 45, 155, 0.4)'
                        }
                      }}>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.84rem', color: '#ffffff', display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <Sparkles size={16} color="#ff2d9b" /> {t('header.proPlan')}
                          </Typography>
                          <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255, 255, 255, 0.7)', mt: 0.2 }}>
                            {t('header.proPlanSubtitle')}
                          </Typography>
                        </Box>
                        <span style={{ color: '#ff2d9b', fontWeight: 800, fontSize: '1.1rem' }}>→</span>
                      </Box>
                    </Link>

                    {/* Selector de idioma integrado */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.2, py: 0.8, borderRadius: '10px', background: 'rgba(255, 255, 255, 0.04)', border: '1px solid rgba(255, 255, 255, 0.08)' }}>
                      <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.7)', fontWeight: 600 }}>
                        {t('header.language')}
                      </Typography>
                      <LanguageSwitcher id="user-menu-language-switcher" />
                    </Box>

                    {/* Enlaces y Acciones con iconos flat */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6, pt: 0.5 }}>
                      <Button
                        fullWidth
                        sx={{
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          color: '#ffffff',
                          borderRadius: '10px',
                          fontSize: '0.84rem',
                          py: 0.8,
                          px: 1.2,
                          gap: 1.2,
                          '&:hover': { background: 'rgba(255, 255, 255, 0.08)' }
                        }}
                        size="small"
                        onClick={() => { userMenuClose(); if (openConfigModal) openConfigModal(0); }}
                      >
                        <Settings size={16} color="#ff2d9b" />
                        {t('header.settings')}
                      </Button>
                      <Link to="/landing" style={{ textDecoration: 'none', color: 'inherit' }} onClick={userMenuClose}>
                        <Button fullWidth sx={{ textTransform: 'none', justifyContent: 'flex-start', color: '#ffffff', borderRadius: '10px', fontSize: '0.84rem', py: 0.8, px: 1.2, gap: 1.2, '&:hover': { background: 'rgba(255, 255, 255, 0.08)' } }} size="small">
                          <Home size={16} color="rgba(255, 255, 255, 0.7)" />
                          {t('nav.home')}
                        </Button>
                      </Link>
                      <Link to="/support" style={{ textDecoration: 'none', color: 'inherit' }} onClick={userMenuClose}>
                        <Button fullWidth sx={{ textTransform: 'none', justifyContent: 'flex-start', color: '#ffffff', borderRadius: '10px', fontSize: '0.84rem', py: 0.8, px: 1.2, gap: 1.2, '&:hover': { background: 'rgba(255, 255, 255, 0.08)' } }} size="small">
                          <MessageSquare size={16} color="rgba(255, 255, 255, 0.7)" />
                          {t('nav.support')}
                        </Button>
                      </Link>
                      <Button
                        fullWidth
                        sx={{
                          textTransform: 'none',
                          justifyContent: 'flex-start',
                          borderRadius: '10px',
                          fontSize: '0.84rem',
                          py: 0.8,
                          px: 1.2,
                          mt: 0.5,
                          color: '#ff4d6d',
                          gap: 1.2,
                          '&:hover': { background: 'rgba(255, 77, 109, 0.12)' }
                        }}
                        size="small"
                        onClick={() => { userMenuClose(); handleGoogleSignOut(); }}
                      >
                        <LogOut size={16} color="#ff4d6d" />
                        {t('header.signOut')}
                      </Button>
                    </Box>
                  </Box>
                </Popover>
              </Box>
            </>
          }
        </Toolbar>
      </AppBar>
      <AboutDialog show={openAboutModal} close={handleCloseAboutModal} />
      <DualScreenTutorialDialog open={openDualScreenModal} onClose={() => setOpenDualScreenModal(false)} />
      <UsageTutorialDialog
        open={openUsageModal}
        onClose={() => setOpenUsageModal(false)}
        onStartTour={() => startInteractiveTour(true)}
      />

      {/* Modal de Código QR para el Control Remoto Móvil */}
      <Dialog
        open={showRemoteModal}
        onClose={() => setShowRemoteModal(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            background: 'rgba(16, 18, 30, 0.96)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(0, 255, 213, 0.35)',
            borderRadius: '24px',
            maxWidth: '450px',
            width: '92%',
            textAlign: 'center',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.85), 0 0 35px rgba(0, 255, 213, 0.18)',
            color: '#ffffff',
            position: 'relative',
            overflow: 'hidden',
            m: { xs: 2, sm: 'auto' },
          }
        }}
      >
        {/* Botón de cierre en la esquina superior derecha */}
        <IconButton
          onClick={() => setShowRemoteModal(false)}
          aria-label="Cerrar modal"
          sx={{
            position: 'absolute',
            top: 14,
            right: 14,
            color: 'rgba(255, 255, 255, 0.6)',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '50%',
            p: '6px',
            transition: 'all 0.2s ease',
            zIndex: 10,
            '&:hover': {
              color: '#ffffff',
              background: 'rgba(255, 255, 255, 0.12)',
              borderColor: 'rgba(255, 255, 255, 0.25)',
              transform: 'scale(1.08)',
            }
          }}
        >
          <X size={17} />
        </IconButton>

        <DialogContent
          sx={{
            p: 0,
            padding: { xs: '24px 20px 20px 20px', sm: '28px 28px 24px 28px' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            boxSizing: 'border-box'
          }}
        >
          <Box
            sx={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #00ffd5, #00b4d8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0a0a0f',
              boxShadow: '0 0 20px rgba(0, 255, 213, 0.45)',
              mb: 1.2,
            }}
          >
            <Smartphone size={22} />
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.6, fontSize: '1.2rem', letterSpacing: '-0.2px' }}>
            Control Remoto Móvil
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.65)',
              fontSize: '0.82rem',
              lineHeight: 1.45,
              mb: 2,
              maxWidth: '360px',
              px: 1
            }}
          >
            Escaneá este código QR con tu smartphone para controlar la fiesta, buscar canciones y lanzar efectos a la pantalla principal remotamente.
          </Typography>

          <Box
            sx={{
              p: 1.5,
              background: '#ffffff',
              borderRadius: '16px',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.45)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <QRCodeSVG value={getRemoteUrl()} size={160} level="M" />
          </Box>

          <CopyableUrl url={getRemoteUrl()} maxWidth="360px" sx={{ mb: 2 }} />

          <Button
            variant="outlined"
            onClick={() => setShowRemoteModal(false)}
            sx={{
              maxWidth: '360px',
              width: '100%',
              py: 0.9,
              borderColor: 'rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              borderRadius: '12px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.88rem',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#00ffd5',
                color: '#00ffd5',
                background: 'rgba(0, 255, 213, 0.08)',
                boxShadow: '0 0 14px rgba(0, 255, 213, 0.2)',
              }
            }}
          >
            Cerrar
          </Button>
        </DialogContent>
      </Dialog>

      {/* Modal de Dispositivos Conectados en Vivo */}
      <ConnectedDevicesModal
        open={openConnectedDevicesModal}
        onClose={() => setOpenConnectedDevicesModal(false)}
        hostId={user?.uid}
        onOpenQr={() => showHideModalQR && showHideModalQR()}
      />
    </>
  );
}
